import { useQuery } from '@apollo/client';
import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material';
import { Autocomplete, Box, Checkbox, CircularProgress, TextField } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Count } from '~/components/count';
import { SearchBar } from '~/components/forms/search-bar';
import { AssetSummaryTable } from '../components/AssetSummaryTable';
import { ListTabHeader, LoadingSpinner, zoneDisplayLabel } from '../lib';
import { PlayoutReportByAssetDocument } from '../queries/PlayoutReportByAsset.generated';
import { usePlayoutReport } from './context';

export const PlayoutReportsByAsset = () => {
  const { t } = useTranslation();
  const { variables, querySkipped } = usePlayoutReport();
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search')?.trim() ?? '';

  const { data, loading } = useQuery(PlayoutReportByAssetDocument, {
    skip: querySkipped,
    fetchPolicy: 'cache-and-network',
    variables,
  });

  const byAssetSummary = useMemo(() => data?.network?.playoutReport.byAssetSummary ?? [], [data]);

  const assetTypes = useMemo(() => {
    const zones = new Set<string>();
    byAssetSummary.forEach((row) => row.zones.forEach((zone) => zones.add(zone)));
    return [...zones]
      .map((zone) => ({ zone, label: zoneDisplayLabel(zone) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [byAssetSummary]);

  // Selected asset types, persisted in the URL alongside the search (empty =
  // all). Intersected with the zones present in the data so a stale zone in a
  // shared link can't invisibly filter the table while the input claims
  // "All Asset Types" — both the Autocomplete and the row filter read this.
  const typeParam = searchParams.get('assetTypes');
  const selectedTypes = useMemo(() => {
    const urlZones = new Set((typeParam ?? '').split(',').filter(Boolean));
    return assetTypes.filter((type) => urlZones.has(type.zone));
  }, [assetTypes, typeParam]);

  const setSelectedTypes = (zones: string[]) =>
    setSearchParams((params) => {
      if (zones.length === 0) {
        params.delete('assetTypes');
      } else {
        params.set('assetTypes', zones.join(','));
      }
      params.set('page', '1');
      return params;
    });

  const filteredAssets = useMemo(() => {
    const query = search.trim().toLowerCase();
    const activeZones = new Set(selectedTypes.map((type) => type.zone));
    return byAssetSummary.filter(
      (row) =>
        (!query || row.asset.toLowerCase().includes(query)) &&
        // Assets can belong to multiple zones (shown as a comma-separated list),
        // so match when any of the asset's zones is in the selected set.
        (activeZones.size === 0 || row.zones.some((zone) => activeZones.has(zone))),
    );
  }, [byAssetSummary, search, selectedTypes]);

  return (
    <Box>
      <ListTabHeader>
        <Box sx={{ width: 'min(280px, 100%)' }}>
          <SearchBar
            placeholder={t('pages.playout_reports.by_asset.search_placeholder')}
            search={search}
            onChange={(value) =>
              setSearchParams((params) => {
                params.set('search', value);
                params.set('page', '1');
                return params;
              })
            }
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            '& .MuiOutlinedInput-root': { minHeight: 40 },
          }}
        >
          {assetTypes.length > 0 && (
            <Autocomplete
              autoHighlight
              disableCloseOnSelect
              multiple
              limitTags={5}
              size="small"
              options={assetTypes}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(o, v) => o.zone === v.zone}
              value={selectedTypes}
              onChange={(_, selected) => setSelectedTypes(selected.map((o) => o.zone))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('pages.playout_reports.top_assets.columns.asset_type')}
                  placeholder={
                    selectedTypes.length === 0
                      ? t('pages.playout_reports.by_asset.all_asset_types')
                      : undefined
                  }
                />
              )}
              renderOption={(props, option, { selected }) => (
                <Box component="li" {...props} key={option.zone}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlank fontSize="small" />}
                    checkedIcon={<CheckBox fontSize="small" />}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option.label}
                </Box>
              )}
              slotProps={{
                popper: {
                  style: { width: 'fit-content', minWidth: 'min(320px, 100vw)' },
                  placement: 'bottom-end',
                },
              }}
              sx={{ width: 'min(320px, 100%)' }}
            />
          )}
          <Count
            selectedCount={0}
            totalCount={filteredAssets.length}
            thing="common.pluralize.asset_plural"
          />
        </Box>
      </ListTabHeader>
      {loading && !data ? (
        <LoadingSpinner>
          <CircularProgress aria-label={t('common.status.loading')} size={60} thickness={4} />
        </LoadingSpinner>
      ) : (
        <AssetSummaryTable
          columnStateKey="byAsset"
          data={filteredAssets}
          emptyLabel={t('pages.playout_reports.by_asset.empty')}
        />
      )}
    </Box>
  );
};
